/**
 * Runs a full origin chain (data/story/originFull.ts, e.g. origin B in data/story/originB.ts) from the wake to the
 * sect choice. Not a system of its own: systems/originStory.ts makes one when the player's origin has a full chain and
 * hands it setup, talk, refuse, observe and step. Every quest starts the moment the one before is done and hands in on
 * its own (no walk-backs); progress is `og-done-<id>` scene marks.
 *
 * Objectives: `talk` (a chain person, or the ferryman), `visit` (a spot, mounted or on water), `kill` (monster id),
 * `block` / `guardBreak` (the player's hits), `route` (checkpoints in order; lantern watchers or a clock send it back),
 * `escort` (lead a person along a path; waves on the way), `duelWin` (the bout's result), `sect` (a school joined, or
 * "walk alone" picked: `plainsStory.sect = 'wanderer'`, the same record origin A's fair keeps), `dodge` / `counter` (a blow
 * on the hero passed by a dash or answered by a counter). A step's `bout` (origin D's challenger queue): talking to the
 * challenger starts a fair bout in its style and level (the duel system's tournament path).
 * Deterministic: no random draws; time only from `step(dt)`.
 */
import { bySex } from '../data/plainsOpening';
import { getSchool } from '../data/schools';
import { ogDone, type FullChainDef, type FullNpc, type FullStep, type Watcher, type XY } from '../data/story/originFull';
import type { QuestDef } from '../data/quests';
import { nearestFloor } from '../path';
import { isWaterAt } from '../qinggong';
import { duelMatchOf } from './duelMatch';
import type { Entity, PlayerCommand, World, WorldEvent } from '../world';
import type { SystemHost } from './types';

type Count = 'talk' | 'visit' | 'kill' | 'block' | 'guardBreak' | 'route' | 'escort' | 'duelWin' | 'sect' | 'dodge' | 'counter';

const RUNNERS = new WeakMap<object, OriginFullRunner>();
/** The full origin chain of a World, if its player's origin has one (for the client's quest strip and the tests). */
/** Whether the shared quest log's plains quest (origin A's elder chain) is shown: always for A, for the others once taken. */
export const showsPlainsQuest = (origin: string, status: string): boolean => origin === 'a' || status !== 'available';
export const originFullOf = (w: object): OriginFullRunner | null => RUNNERS.get(w) ?? null;

const startMark = (o: string): string => `og-start-${o}`;
const WALK_ALONE = 'เดินคนเดียว';

interface WatchState {
  w: Watcher;
  e: Entity;
  facing: 1 | -1;
  t: number;
  warned: boolean;
}

export class OriginFullRunner {
  private readonly people = new Map<string, Entity>();
  private readonly idOf = new Map<number, string>();
  private readonly idle = new Map<string, number>();
  /** The step under way (its id once begun) and its counts, one per objective. */
  private cur: string | null = null;
  readonly prog: number[] = [];
  private readonly talked = new Set<string>();
  private timer = 0;
  private readonly spawned = new Set<number>();
  private readonly gone = new Map<number, number>();
  private watch: WatchState[] = [];
  private escortIx = 0;
  private escortWaitT = 0;
  private readonly waves = new Set<number>();
  private readonly waveMobs = new Set<number>();
  private wandererOffered = false;
  /** Story rivals found by name (`duelRival`): the duel system renames its bout foe, so the name and title are kept. */
  private readonly locked = new Map<number, { name: string; title: string }>();

  constructor(
    private readonly h: SystemHost,
    readonly def: FullChainDef,
  ) {}

  private get w(): World {
    return this.h.world;
  }

  private get zone(): string {
    return this.w.map.zone as string;
  }

  private sex(): 'm' | 'f' {
    return this.w.progress.appearance.sex === 'f' ? 'f' : 'm';
  }

  private floor(at: XY): { x: number; y: number } {
    const f = nearestFloor(this.w.map, at[0], at[1], 4);
    return f ? { x: f[0], y: f[1] } : { x: at[0], y: at[1] };
  }

  private done(id: string): boolean {
    return this.w.progress.scenes.includes(ogDone(id));
  }

  /** The step the chain is on (null when all are done). */
  current(): FullStep | null {
    return this.def.steps.find((s) => !this.done(s.quest.id)) ?? null;
  }

  /** The quest under way and its counts, for the quest strip (null when none runs here). */
  get quest(): { quest: QuestDef; progress: readonly number[] } | null {
    const s = this.current();
    return s && this.cur === s.quest.id ? { quest: s.quest, progress: this.prog } : null;
  }

  /** A chain person by id (tests, the client's marker). */
  person(id: string): Entity | undefined {
    return this.people.get(id);
  }

  private chosen(): boolean {
    const pr = this.w.progress;
    return !!pr.school || !!pr.plainsStory?.sect;
  }

  private stepIx(id: string): number {
    return this.def.steps.findIndex((s) => s.quest.id === id);
  }

  /** Whether a person is out now (their `from` step is reached). */
  private present(n: FullNpc): boolean {
    if (n.zone !== this.zone) return false;
    if (!n.from) return true;
    const c = this.current();
    const at = c ? this.stepIx(c.quest.id) : this.def.steps.length;
    return at >= this.stepIx(n.from);
  }

  // ───────────────────────── hooks ─────────────────────────

  setup(): void {
    RUNNERS.set(this.w, this);
    if (this.w.map.interior) return;
    const first = this.def.steps[0]!;
    const pr = this.w.progress;
    if (!pr.scenes.includes(startMark(this.def.origin)) && this.current() === first && this.zone === first.zone && first.startAt) {
      pr.scenes.push(startMark(this.def.origin));
      Object.assign(this.w.player, this.floor(first.startAt));
    }
    this.ensurePeople();
  }

  private ensurePeople(): void {
    for (const n of this.def.npcs) {
      if (this.people.has(n.id) || !this.present(n)) continue;
      const at = this.floor(n.after && this.done(n.after.step) ? n.after.to : n.at);
      const e = this.h.add({ kind: 'npc', x: at.x, y: at.y, name: n.name, title: n.title, sprite: n.sprite, facing: n.facing ?? 1 });
      this.people.set(n.id, e);
      this.idOf.set(e.id, n.id);
    }
  }

  /** `role`: a person of another system's (originStory's ferryman). False: not this chain's to answer. */
  talk(npc: Entity, role?: string): boolean {
    const id = this.idOf.get(npc.id) ?? role;
    if (!id) return false;
    const n = this.def.npcs.find((x) => x.id === id);
    const s = this.current();
    const live = s && this.cur === s.quest.id;
    const as = n?.talkAs ?? id;
    if (live && s.bout?.npc === id && n) return this.startBout(s, npc);
    const wanted = live && !this.talked.has(id) && this.open(s, 'talk', as);
    if (wanted) {
      this.talked.add(id);
      if (s.grantMount?.npc === id) this.grantMount(s.grantMount.mount);
      if (s.grantQinggong) this.grantQinggong(s.grantQinggong);
      this.say(npc, s.talkLines?.[id] ?? s.quest.done, null);
      this.count('talk', as);
      return true;
    }
    if (!n) return false;
    if (n.school) {
      const sc = getSchool(n.school);
      const pr = this.w.progress;
      if (pr.school === n.school) return this.say(npc, sc.memberText, null);
      if (this.chosen()) return this.say(npc, this.def.lines.chosenAlready, null);
      this.h.emit({ type: 'dialog', npcId: npc.id, text: `“${sc.motto}” ${sc.joinText}`, action: 'joinSchool', schoolId: n.school, label: 'เข้าร่วมสำนัก' });
      return true;
    }
    if (n.wanderer && !this.chosen()) {
      this.say(npc, `${n.idle[0] ?? ''} · ถ้าจะ${WALK_ALONE} บอกข้าได้`, 'accept', WALK_ALONE);
      this.wandererOffered = true;
      return true;
    }
    const k = this.idle.get(id) ?? 0;
    this.idle.set(id, k + 1);
    return this.say(npc, n.idle[k % Math.max(1, n.idle.length)] ?? this.def.lines.allDone, null);
  }

  refuse(cmd: PlayerCommand): boolean {
    if (cmd.t !== 'questAccept' || !this.wandererOffered) return false;
    this.wandererOffered = false;
    if (this.chosen()) return true;
    const pr = this.w.progress;
    pr.plainsStory = { ...pr.plainsStory, sect: 'wanderer' };
    this.h.log('sys', `เลือก${WALK_ALONE}`);
    const tie = [...this.people.entries()].find(([k]) => this.def.npcs.find((x) => x.id === k)?.wanderer)?.[1];
    if (tie) this.h.emit({ type: 'bark', id: tie.id, text: bySex(this.def.lines.wandererPick, this.sex()) });
    this.h.emit({ type: 'progressChanged' });
    this.count('sect');
    return true;
  }

  observe(ev: WorldEvent): void {
    const w = this.w;
    switch (ev.type) {
      case 'dialog': {
        const id = this.idOf.get(ev.npcId);
        if (!id || !this.def.npcs.find((x) => x.id === id)?.wanderer) this.wandererOffered = false;
        break;
      }
      case 'hit': {
        // The hero by `primaryId`: in a bout the World steps the rival as a second player (`w.player` is then it).
        const me = w.primaryId;
        if (ev.tgtId === me && ev.outcome === 'blocked') this.count('block');
        if (ev.srcId === me && (ev.outcome === 'guardBreak' || ev.outcome === 'guardShatter')) this.count('guardBreak');
        if (ev.tgtId === me && ev.outcome === 'dodge') this.count('dodge');
        if (ev.tgtId === me && ev.outcome === 'countered') this.count('counter');
        break;
      }
      case 'death': {
        const e = w.get(ev.id);
        if (e?.kind !== 'monster' || !e.brain) break;
        this.count('kill', e.brain.defId);
        if (this.spawned.has(e.id)) this.gone.set(e.id, 1.2);
        break;
      }
      case 'joinedSchool': {
        const npc = [...this.people.entries()].find(([k]) => this.def.npcs.find((x) => x.id === k)?.school === ev.school)?.[1];
        if (npc) this.h.emit({ type: 'bark', id: npc.id, text: this.def.lines.joined });
        this.count('sect');
        break;
      }
      case 'pvpDuel':
        if (ev.phase === 'result' && ev.winner === w.primaryId) this.count('duelWin');
        break;
    }
  }

  step(dt: number): void {
    const w = this.w;
    if (w.map.interior) return;
    this.ensurePeople();
    this.keepNames();
    this.walkFreed(dt);
    for (const [id, t] of this.gone) {
      if (t - dt > 0) this.gone.set(id, t - dt);
      else {
        this.gone.delete(id);
        this.spawned.delete(id);
        w.entities.delete(id);
      }
    }
    const s = this.current();
    if (!s || s.zone !== this.zone) return;
    if (this.cur !== s.quest.id) this.begin(s);
    if (s.timer) {
      this.timer -= dt;
      if (this.timer <= 0) return this.fail(s.timer.failTo, s.timer.failText, true);
    }
    s.quest.objectives.forEach((o, i) => {
      if (!this.open(s, o.type, o.target, i)) return;
      if (o.type === 'visit') {
        const sp = this.def.spots[o.target ?? ''];
        if (sp && this.inSpot(sp.at, sp.r, sp.mount, sp.water)) this.count('visit', o.target);
      } else if (o.type === 'route') this.stepRoute(o.target ?? '', i, dt);
      else if (o.type === 'escort') this.stepEscort(o.target ?? '', dt);
    });
    if (s.bout) this.boutRival(s);
    else if (s.quest.objectives.some((o) => o.type === 'duelWin') && this.def.duelRival) {
      const d = duelMatchOf(w);
      const r = [...w.entities.values()].find((e) => e.kind === 'npc' && e.name === this.def.duelRival!.name);
      if (d && r && d.rivalId < 0) d.rivalId = r.id;
      if (r && !this.locked.has(r.id)) this.locked.set(r.id, { name: r.name, title: r.title });
    }
  }

  // ───────────────────────── bouts (a step's `bout`) ─────────────────────────

  /** Starts the step's bout against `npc` (a fair bout in the challenger's style and level), or says why not. */
  private startBout(s: FullStep, npc: Entity): true {
    const b = s.bout!;
    const d = duelMatchOf(this.w);
    if (!d) return this.say(npc, s.quest.remind, null);
    if (d.active) return true;
    d.rivalId = npc.id;
    const why = d.startTourney({ name: npc.name, styleId: b.styleId, level: b.level, sprite: npc.sprite || 'heroBlood', label: b.label });
    if (why) this.h.notice(why);
    return true;
  }

  /** While it is a challenger's turn, it is the duel system's rival (an ordinary challenge on it works too). */
  private boutRival(s: FullStep): void {
    const d = duelMatchOf(this.w);
    const e = this.people.get(s.bout!.npc);
    if (d && e && !d.active && d.rivalId !== e.id && this.w.entities.has(e.id)) d.rivalId = e.id;
  }

  /** The duel system renames its rival after a bout: the chain's people keep their own names. */
  private keepNames(): void {
    for (const [id, l] of this.locked) {
      const e = this.w.entities.get(id);
      if (e && (e.name !== l.name || e.title !== l.title)) Object.assign(e, l);
    }
    for (const [id, e] of this.people) {
      const n = this.def.npcs.find((x) => x.id === id);
      if (n && (e.name !== n.name || e.title !== n.title)) Object.assign(e, { name: n.name, title: n.title });
    }
  }

  // ───────────────────────── the step ─────────────────────────

  private begin(s: FullStep): void {
    this.cur = s.quest.id;
    this.prog.length = 0;
    s.quest.objectives.forEach(() => this.prog.push(0));
    this.talked.clear();
    this.timer = s.timer?.seconds ?? 0;
    this.clearWatch();
    for (const sp of s.spawns ?? []) {
      const at = this.floor(sp.at);
      const m = this.h.spawnMonster(sp.monster, at.x, at.y);
      if (sp.name) m.name = sp.name;
      if (sp.sprite) m.sprite = sp.sprite;
      this.spawned.add(m.id);
    }
    for (const o of s.quest.objectives) {
      const r = o.type === 'route' ? this.def.routes[o.target ?? ''] : undefined;
      for (const wd of r?.watchers ?? []) {
        const at = this.floor(wd.at);
        const e = this.h.add({ kind: 'npc', x: at.x, y: at.y, name: wd.name ?? 'ยามถือตะเกียง', title: wd.title ?? 'หันตามเสียงกระแอม', sprite: wd.sprite ?? 'guardOfficer', facing: wd.startFacing });
        this.watch.push({ w: wd, e, facing: wd.startFacing, t: 0, warned: false });
      }
      const es = o.type === 'escort' ? this.def.escorts[o.target ?? ''] : undefined;
      if (es) {
        this.escortIx = 1;
        this.waves.clear();
        this.waveMobs.clear();
        const e = this.people.get(es.npc);
        if (e) Object.assign(e, this.floor(es.path[0]!));
      }
    }
    const q = s.quest;
    this.h.emit({ type: 'quest', status: 'accepted', questId: q.id });
    this.h.log('sys', `ภารกิจ「${q.title}」 ${bySex(q.offer, this.sex())}`);
    this.h.notice(`ภารกิจใหม่ · ${q.title} · ${bySex(q.remind, this.sex())}`);
    const giver = this.def.npcs.find((n) => n.name === q.giver?.name);
    const ge = giver && this.people.get(giver.id);
    if (ge) this.say(ge, q.offer, null);
    this.guide(s);
    if (q.objectives.some((o) => o.type === 'sect') && this.chosen()) this.count('sect');
  }

  private guide(s: FullStep): void {
    const g = s.guide;
    let at: { x: number; y: number } | undefined;
    if (g?.spot) {
      const sp = this.def.spots[g.spot];
      if (sp) at = { x: sp.at[0], y: sp.at[1] };
    } else if (g?.npc === 'duel' && this.def.duelRival) {
      at = [...this.w.entities.values()].find((e) => e.kind === 'npc' && e.name === this.def.duelRival!.name);
    } else if (g?.npc) at = this.people.get(g.npc) ?? [...this.w.entities.values()].find((e) => e.kind === 'npc' && e.name === this.def.npcs.find((n) => n.id === g.npc)?.name);
    if (at) this.w.story.guide = { x: at.x, y: at.y };
  }

  /** Whether objective `i` (or the first of `type`/`target`) can still count: not full and, when ordered, its turn. */
  private open(s: FullStep, type: string, target?: string, i?: number): boolean {
    const objs = s.quest.objectives;
    const ix = i ?? objs.findIndex((o, k) => o.type === type && (!o.target || o.target === target) && (this.prog[k] ?? 0) < o.count);
    const o = objs[ix];
    if (!o || o.type !== type || (this.prog[ix] ?? 0) >= o.count) return false;
    if (target !== undefined && o.target && o.target !== target) return false;
    return !s.ordered || objs.slice(0, ix).every((x, k) => (this.prog[k] ?? 0) >= x.count);
  }

  /** Counts one event on the step under way; hands it in when every objective is full. */
  private count(type: Count, target?: string): void {
    const s = this.current();
    if (!s || this.cur !== s.quest.id || s.zone !== this.zone) return;
    const objs = s.quest.objectives;
    const ix = objs.findIndex((o, k) => o.type === type && (!o.target || o.target === target) && (this.prog[k] ?? 0) < o.count);
    if (ix < 0 || !this.open(s, type, target, ix)) return;
    this.prog[ix] = (this.prog[ix] ?? 0) + 1;
    if (objs.every((o, k) => (this.prog[k] ?? 0) >= o.count)) this.finish(s);
    else this.h.emit({ type: 'quest', status: 'progress', questId: s.quest.id });
  }

  private finish(s: FullStep): void {
    const q = s.quest;
    const w = this.w;
    w.progress.scenes.push(ogDone(q.id));
    this.cur = null;
    this.clearWatch();
    for (const id of this.spawned) if (!this.gone.has(id)) w.entities.delete(id);
    this.spawned.clear();
    this.h.grantReward({ gold: q.rewards.gold, cultivation: q.rewards.cultivation });
    this.h.emit({ type: 'quest', status: 'completed', questId: q.id });
    this.h.notice(`สำเร็จ · ${q.title}: ${bySex(q.done, this.sex())}`);
    w.story.guide = null;
    if (!this.current()) this.h.log('sys', this.def.lines.allDone);
  }

  /** Back to a spot: a route caught by a lantern, or a clock run out (`whole`: the step starts over). */
  private fail(to: XY, text: string, whole: boolean): void {
    const p = this.w.player;
    Object.assign(p, this.floor(to));
    p.path = null;
    this.h.notice(text);
    const s = this.current();
    if (!s) return;
    if (whole) {
      this.prog.fill(0);
      this.talked.clear();
      this.timer = s.timer?.seconds ?? 0;
    } else
      s.quest.objectives.forEach((o, i) => {
        if (o.type === 'route') this.prog[i] = 0;
      });
  }

  private clearWatch(): void {
    for (const s of this.watch) this.w.entities.delete(s.e.id);
    this.watch = [];
  }

  private inSpot(at: XY, r: number, mount?: string, water?: boolean): boolean {
    const p = this.w.player;
    if (Math.hypot(p.x - at[0], p.y - at[1]) > r) return false;
    if (mount && p.mount !== mount) return false;
    return !water || isWaterAt(this.w.map, p.x, p.y);
  }

  private stepRoute(id: string, i: number, dt: number): void {
    const r = this.def.routes[id];
    if (!r) return;
    const p = this.w.player;
    for (const s of this.watch) {
      s.t += dt;
      if (!s.warned && s.t >= s.w.period - s.w.warn) {
        s.warned = true;
        this.h.emit({ type: 'bark', id: s.e.id, text: 'แค่ก… (กำลังจะหัน)' });
      }
      if (s.t >= s.w.period) {
        s.t = 0;
        s.warned = false;
        s.facing = s.facing === 1 ? -1 : 1;
        s.e.facing = s.facing;
      }
      const dx = p.x - s.e.x;
      const lit = Math.sign(dx) === s.facing && Math.abs(dx) <= s.w.range && Math.abs(p.y - s.e.y) <= s.w.band;
      if (lit && (this.prog[i] ?? 0) > 0) {
        this.h.emit({ type: 'bark', id: s.e.id, text: 'ใครน่ะ!' });
        return this.fail(r.resetTo, r.failText, false);
      }
    }
    const k = this.prog[i] ?? 0;
    const pt = r.points[k];
    if (pt && Math.hypot(p.x - pt.at[0], p.y - pt.at[1]) <= pt.r) this.count('route', id);
  }

  private stepEscort(id: string, dt: number): void {
    const es = this.def.escorts[id];
    const e = es && this.people.get(es.npc);
    if (!es || !e) return;
    const w = this.w;
    const p = w.player;
    this.escortWaitT = Math.max(0, this.escortWaitT - dt);
    for (const m of this.waveMobs) if (!w.get(m) || w.get(m)!.dead) this.waveMobs.delete(m);
    if (this.waveMobs.size > 0) return;
    if (Math.hypot(p.x - e.x, p.y - e.y) > es.leadRange) {
      if (this.escortWaitT <= 0) {
        this.escortWaitT = 6;
        this.h.emit({ type: 'bark', id: e.id, text: es.waitText });
      }
      return;
    }
    const to = es.path[this.escortIx];
    if (!to) return this.count('escort', id);
    const dx = to[0] - e.x;
    const dy = to[1] - e.y;
    const d = Math.hypot(dx, dy);
    if (d < 0.3) {
      const wave = es.waves.find((v) => v.atPathIndex === this.escortIx);
      if (wave && !this.waves.has(this.escortIx)) {
        this.waves.add(this.escortIx);
        for (let n = 0; n < wave.n; n++) {
          const at = this.floor([to[0] + 3, to[1] - 2 + n * 4]);
          const m = this.h.spawnMonster(wave.monster, at.x, at.y);
          this.spawned.add(m.id);
          this.waveMobs.add(m.id);
        }
        this.h.emit({ type: 'bark', id: e.id, text: 'ระวัง! มันซุ่มอยู่' });
        return;
      }
      this.escortIx++;
      if (this.escortIx >= es.path.length) this.count('escort', id);
      return;
    }
    const step = Math.min(d, es.speed * dt);
    if (!this.h.move(e, (dx / d) * step, (dy / d) * step)) Object.assign(e, { x: e.x + (dx / d) * step, y: e.y + (dy / d) * step });
  }

  /** Freed people walk to where they wait (the prisoners to the plaza). */
  private walkFreed(dt: number): void {
    const esc = this.current()?.quest.objectives.find((o) => o.type === 'escort')?.target;
    for (const n of this.def.npcs) {
      if (!n.after || !this.done(n.after.step) || n.id === this.def.escorts[esc ?? '']?.npc) continue;
      const e = this.people.get(n.id);
      if (!e) continue;
      if (this.def.steps.slice(this.stepIx(n.after.step) + 1).some((s) => this.done(s.quest.id) && s.quest.objectives.some((o) => o.type === 'escort' && this.def.escorts[o.target ?? '']?.npc === n.id))) continue;
      const dx = n.after.to[0] - e.x;
      const dy = n.after.to[1] - e.y;
      const d = Math.hypot(dx, dy);
      if (d < 0.2) continue;
      const st = Math.min(d, 2.4 * dt);
      if (!this.h.move(e, (dx / d) * st, (dy / d) * st)) Object.assign(e, { x: e.x + (dx / d) * st, y: e.y + (dy / d) * st });
    }
  }

  private say(npc: Entity, text: string, action: 'accept' | null, label?: string): true {
    this.h.emit({
      type: 'dialog',
      npcId: npc.id,
      text: bySex(text, this.sex()),
      action,
      ...(label ? { label } : {}),
    });
    return true;
  }

  private grantMount(mount: string): void {
    const pr = this.w.progress;
    if (pr.mounts.includes(mount)) return;
    pr.mounts.push(mount);
    pr.mountPick = mount;
    this.h.emit({ type: 'progressChanged' });
  }

  private grantQinggong(id: string): void {
    const pr = this.w.progress;
    if ((pr.qinggong[id] ?? 0) >= 1) return;
    pr.qinggong[id] = 1;
    this.h.emit({ type: 'progressChanged' });
  }
}
